import { useEffect, useRef, useState } from "react";
import { t } from "../lib/uiStrings";
import { tx } from "../lib/tx";

type MoveDir = "fwd" | "back" | "left" | "right";

function send(detail: Record<string, unknown>) {
  window.dispatchEvent(new CustomEvent("cf-fp-control", { detail }));
}

/**
 * On-screen control HUD for walkable rooms: hold-to-walk pad, hold-to-look
 * buttons, an interact button (e.g. "Read note"), and a mouse capture/release
 * toggle. Everything works WITHOUT pointer lock (drag-to-look on the canvas is
 * also supported), so nothing is unreachable while walking. While the mouse IS
 * captured, DOM buttons can't be clicked — the HUD collapses to a hint.
 */
export function WalkControls({
  interactLabel,
  interactEnabled,
  onInteract,
  showExtras = true,
}: {
  interactLabel: string;
  interactEnabled: boolean;
  onInteract: () => void;
  /** False for courses with no `deskPc`/`conferenceTv` wired anywhere (see
   *  `ScenarioSpec.walkHudMinimal`) — hides the always-disabled "Nothing to
   *  interact with nearby" button and the "Capture mouse" button, since
   *  neither has any real target/use in that course. The move/look pad and
   *  drag-to-look hint stay either way. */
  showExtras?: boolean;
}) {
  const [locked, setLocked] = useState<boolean>(() => !!document.pointerLockElement);
  const [padsOpen, setPadsOpen] = useState<boolean>(false);
  const turnTimer = useRef<number>(0);

  useEffect(() => {
    const onLock = () => setLocked(!!document.pointerLockElement);
    document.addEventListener("pointerlockchange", onLock);
    return () => {
      document.removeEventListener("pointerlockchange", onLock);
      window.clearInterval(turnTimer.current);
      send({ kind: "stopall" });
    };
  }, []);

  const stopTurn = () => window.clearInterval(turnTimer.current);

  const holdMove = (dir: MoveDir) => ({
    onPointerDown: (e: React.PointerEvent) => {
      e.preventDefault();
      (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
      send({ kind: "move", dir, down: true });
    },
    onPointerUp: () => send({ kind: "move", dir, down: false }),
    onPointerLeave: () => send({ kind: "move", dir, down: false }),
    onPointerCancel: () => send({ kind: "move", dir, down: false }),
  });

  const holdTurn = (dx: number, dy: number) => ({
    onPointerDown: (e: React.PointerEvent) => {
      e.preventDefault();
      (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
      send({ kind: "turn", dx, dy });
      stopTurn();
      turnTimer.current = window.setInterval(() => send({ kind: "turn", dx, dy }), 40);
    },
    onPointerUp: stopTurn,
    onPointerLeave: stopTurn,
    onPointerCancel: stopTurn,
  });

  if (locked) {
    return (
      <div className="cf-walk-hud cf-walk-hud--locked" role="status">
        {t("walk.mouseCaptured")}
      </div>
    );
  }

  return (
    <div className="cf-walk-hud" role="group" aria-label={tx("Movement controls")}>
      <button
        type="button"
        className="cf-walk-hud__collapse"
        aria-expanded={padsOpen}
        onClick={() => setPadsOpen((v) => !v)}
      >
        {t("walk.controls")} {padsOpen ? "▾" : "▸"}
      </button>

      {padsOpen && (
        <div className="cf-walk-hud__pads">
          <div className="cf-walk-hud__pad" aria-label={tx("Walk")}>
            <span className="cf-walk-hud__padlabel">{t("walk.walk")}</span>
            <div className="cf-walk-hud__grid">
              <span />
              <button type="button" title={t("walk.forward")} {...holdMove("fwd")}>▲</button>
              <span />
              <button type="button" title={t("walk.stepLeft")} {...holdMove("left")}>◀</button>
              <button type="button" title={t("walk.back")} {...holdMove("back")}>▼</button>
              <button type="button" title={t("walk.stepRight")} {...holdMove("right")}>▶</button>
            </div>
          </div>
          <div className="cf-walk-hud__pad" aria-label={tx("Look")}>
            <span className="cf-walk-hud__padlabel">{t("walk.look")}</span>
            <div className="cf-walk-hud__grid">
              <span />
              <button type="button" title={t("walk.lookUp")} {...holdTurn(0, -0.05)}>▲</button>
              <span />
              <button type="button" title={t("walk.turnLeft")} {...holdTurn(-0.07, 0)}>◀</button>
              <button type="button" title={t("walk.lookDown")} {...holdTurn(0, 0.05)}>▼</button>
              <button type="button" title={t("walk.turnRight")} {...holdTurn(0.07, 0)}>▶</button>
            </div>
          </div>
        </div>
      )}

      {showExtras && (
        <button
          type="button"
          className="cf-walk-hud__interact"
          disabled={!interactEnabled}
          onClick={onInteract}
          title={interactEnabled ? interactLabel : t("walk.walkCloser")}
        >
          {interactEnabled ? interactLabel : t("walk.nothingNearby")}
        </button>
      )}

      {showExtras && (
        <button
          type="button"
          className="cf-walk-hud__capture"
          onClick={() => {
            const canvas = document.querySelector("canvas.sim-canvas") as HTMLElement | null;
            canvas?.requestPointerLock?.();
          }}
          title={t("walk.captureMouseTitle")}
        >
          {t("walk.captureMouse")}
        </button>
      )}
      <div className="cf-walk-hud__hint">{t("walk.dragHint")}</div>
    </div>
  );
}
